// Customer authentication service for booking service
// Canonical customer shape for ALL booking-service frontends.

export interface CommunicationPreferences {
  emailNotifications: boolean;
  smsNotifications: boolean;
  marketingEmails: boolean;
  reservationReminders: boolean;
  paymentReminders: boolean;
  promotionalOffers: boolean;
}

export interface BookingCustomer {
  id: string;
  /** Home club (for public API scoping: policies, courses, branding). */
  clubId?: string;
  clubNumber?: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  /**
   * High-level category for pricing / permissions.
   * MUST be either 'member' or 'guest'.
   */
  customerType: 'member' | 'guest';
  /**
   * Underlying customer type reference.
   * - In most cases this will be a full object from the API with a `category` field.
   * - In some legacy cases it may be the raw ID string.
   */
  customerTypeId: any;
  customerTypeName?: string | null;
  dateOfBirth?: string | null;
  preferences?: string;
  showNameOnTeeSheet?: boolean;
  communicationPreferences?: CommunicationPreferences;
}

import { getApiBaseUrl } from './api';

const SESSION_KEY = 'booking_service_auth';
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
const API_BASE_URL = getApiBaseUrl();

interface AuthSession {
  token: string;
  customer: BookingCustomer;
  expiresAt: number;
}

export type CustomerClubContext = {
  clubId?: string;
  clubNumber?: string | number;
  bookingSlug?: string;
  clubName?: string;
};

export const customerAuthService = {
  login: async (email: string, password: string, clubContext?: CustomerClubContext) => {
    console.log('🔐 Customer login attempt:', email);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/customer-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          ...(clubContext?.clubId ? { clubId: clubContext.clubId } : {}),
          ...(clubContext?.clubNumber != null && String(clubContext.clubNumber).trim() !== ''
            ? { clubNumber: clubContext.clubNumber }
            : {}),
          ...(clubContext?.bookingSlug?.trim() ? { bookingSlug: clubContext.bookingSlug.trim().toLowerCase() } : {}),
          ...(clubContext?.clubName?.trim() ? { clubName: clubContext.clubName.trim() } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      if (data.success) {
        const { token, customer } = data.data;
        
        // Store session in localStorage
        const session: AuthSession = {
          token,
          customer,
          expiresAt: Date.now() + SESSION_DURATION
        };
        console.log('🔐 Storing login session in localStorage:', session);
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        console.log('🔐 Login session stored successfully');
        
        return { token, customer };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('🔐 Login error:', error);
      // SECURITY: Never bypass authentication - always require valid credentials
      throw new Error(error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
    }
  },
  
  register: async (userData: any) => {
    console.log('🔐 Customer registration attempt:', userData);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/customer-auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      if (data.success) {
        const { token, customer } = data.data;
        
        // Store session in localStorage
        const session: AuthSession = {
          token,
          customer,
          expiresAt: Date.now() + SESSION_DURATION
        };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        
        return { token, customer };
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('🔐 Registration error:', error);
      // SECURITY: Never bypass authentication - always require valid API response
      throw new Error(error instanceof Error ? error.message : 'Registration failed. Please try again.');
    }
  },
  
  logout: async () => {
    // Mock logout
    console.log('Mock logout');
    localStorage.removeItem(SESSION_KEY);
    return { success: true };
  },
  
  getCurrentUser: async () => {
    // Check for existing session in localStorage
    try {
      console.log('🔍 Checking for existing session...');
      const sessionData = localStorage.getItem(SESSION_KEY);
      console.log('🔍 Session data from localStorage:', sessionData ? 'Found' : 'Not found');
      
      if (!sessionData) {
        console.log('🔍 No session data found');
        return null;
      }

      const session: AuthSession = JSON.parse(sessionData);
      console.log('🔍 Parsed session:', session);
      console.log('🔍 Current time:', Date.now());
      console.log('🔍 Session expires at:', session.expiresAt);
      
      // Check if session has expired
      if (Date.now() > session.expiresAt) {
        console.log('🔍 Session expired, removing from localStorage');
        localStorage.removeItem(SESSION_KEY);
        return null;
      }

      // Verify token with backend
      try {
        const response = await fetch(`${getApiBaseUrl()}/customer-auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            console.log('🔍 Token verified, returning customer:', data.data);
            // Update cached session with latest customer data
            const updatedSession: AuthSession = {
              ...session,
              customer: data.data
            };
            localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
            return data.data;
          }
        }
        
        // Token invalid, clear session
        console.log('🔍 Token verification failed, clearing session');
        localStorage.removeItem(SESSION_KEY);
        return null;
      } catch (error) {
        console.error('🔍 Token verification error:', error);
        // On network error, allow using cached session for offline capability
        // But only if the session hasn't expired
        if (Date.now() <= session.expiresAt) {
          console.log('🔍 Network error, using cached session:', session.customer);
          return session.customer;
        } else {
          // Session expired, clear it
          localStorage.removeItem(SESSION_KEY);
          return null;
        }
      }
    } catch (error) {
      console.error('🔍 Error parsing session data:', error);
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  },

  isSessionValid: () => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) {
        return false;
      }

      const session: AuthSession = JSON.parse(sessionData);
      return Date.now() <= session.expiresAt;
    } catch (error) {
      return false;
    }
  },

  // Get cached customer data without API verification (for development)
  getCachedCustomer: () => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) {
        return null;
      }

      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() <= session.expiresAt) {
        return session.customer;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /** Bearer token for customer-scoped API calls (e.g. partner invite claim). */
  getAccessToken: (): string | null => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return null;
      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) return null;
      return session.token ?? null;
    } catch {
      return null;
    }
  },

  updateCommunicationPreferences: async (
    partial: Partial<CommunicationPreferences>
  ): Promise<
    | { ok: true; preferences: CommunicationPreferences }
    | { ok: false; error: string }
  > => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) {
        return { ok: false, error: 'Not signed in' };
      }
      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) {
        return { ok: false, error: 'Session expired' };
      }
      const response = await fetch(`${API_BASE_URL}/customer-auth/communication-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(partial),
      });
      const payload = (await response.json().catch(() => ({}))) as CommunicationPreferences & { error?: string };
      if (!response.ok) {
        return { ok: false, error: payload?.error || 'Update failed' };
      }
      const preferences = payload as CommunicationPreferences;
      const updatedSession: AuthSession = {
        ...session,
        customer: {
          ...session.customer,
          communicationPreferences: preferences,
        },
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
      return { ok: true, preferences };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Update failed' };
    }
  },

  updateProfile: async (patch: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string | null;
    preferences?: string;
    showNameOnTeeSheet?: boolean;
  }): Promise<{ ok: true; customer: BookingCustomer } | { ok: false; error: string }> => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) {
        return { ok: false, error: 'Not signed in' };
      }
      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) {
        return { ok: false, error: 'Session expired' };
      }
      const response = await fetch(`${API_BASE_URL}/customer-auth/profile`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, error: (payload as { error?: string }).error || 'Update failed' };
      }
      if (!payload.success || !payload.data) {
        return { ok: false, error: 'Invalid response' };
      }
      const merged: BookingCustomer = {
        ...session.customer,
        ...payload.data,
      };
      const updatedSession: AuthSession = {
        ...session,
        customer: merged,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
      return { ok: true, customer: merged };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Update failed' };
    }
  },

  cancelMyReservation: async (
    reservationId: string
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return { ok: false, error: 'Not signed in' };
      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) return { ok: false, error: 'Session expired' };
      const response = await fetch(
        `${API_BASE_URL}/customer-auth/reservations/${encodeURIComponent(reservationId)}/cancel`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, error: (payload as { error?: string }).error || 'Cancel failed' };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Cancel failed' };
    }
  },

  updateMyReservationNotes: async (
    reservationId: string,
    notes: string
  ): Promise<{ ok: true } | { ok: false; error: string }> => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return { ok: false, error: 'Not signed in' };
      const session: AuthSession = JSON.parse(sessionData);
      if (Date.now() > session.expiresAt) return { ok: false, error: 'Session expired' };
      const response = await fetch(
        `${API_BASE_URL}/customer-auth/reservations/${encodeURIComponent(reservationId)}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${session.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, error: (payload as { error?: string }).error || 'Update failed' };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Update failed' };
    }
  },
};
